import { useState, useMemo, useEffect } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Search, AlertTriangle, Info, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from 'react-i18next';
import { type StaticHospitalData, expandMapping } from "@/hooks/useStaticHospitalData";
import { HospitalPriceSummary } from "@/components/HospitalPriceSummary";

interface HospitalMappingsTableProps {
  hospitalId: string;
  hospitalName: string;
  staticData?: StaticHospitalData;
  /** True while the static per-hospital JSON is still downloading. */
  staticLoading?: boolean;
}

export function HospitalMappingsTable({ hospitalId, hospitalName, staticData, staticLoading }: HospitalMappingsTableProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const { t } = useTranslation(['hospital', 'common']);

  // Theme-aware badge background (replaces hardcoded #000000a1)
  const badgeBg = isAhp
    ? (isDark ? 'rgba(var(--civic-bg-dark-rgb),0.85)' : 'rgba(0,0,0,0.06)')
    : '#000000a1';
  const disabledBg = isAhp
    ? (isDark ? 'rgba(var(--civic-bg-dark-rgb),0.5)' : 'rgba(0,0,0,0.03)')
    : '#1f1f1f';
  const disabledText = isAhp
    ? (isDark ? 'rgba(var(--civic-cream-rgb),0.3)' : 'rgba(var(--civic-ink-rgb),0.3)')
    : '#6b7280';
  const settingBadgeTextColor = isAhp
    ? (isDark ? 'var(--civic-cream)' : 'var(--civic-ink)')
    : '#ffffff';
  const settingBadgeBorderColor = isAhp
    ? (isDark ? 'rgba(var(--civic-cream-rgb),0.4)' : 'rgba(var(--civic-ink-rgb),0.3)')
    : '#ffffff';
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCodeType, setSelectedCodeType] = useState<string | null>(null);
  const [selectedSetting, setSelectedSetting] = useState<string | null>(null);
  const pageSize = 20;

  // Debounce search term to prevent excessive queries
  const searchTerm = useDebounce(searchInput, 300);

  // Check if we have static data for client-side operation
  const hasStaticMappings = !!(staticData?.mappings && staticData.mappings.length > 0);

  const getCodeType = (mapping: any) => {
    // Priority order: CPT > HCPCS > DRG > REV > SUPPLY > RX > OTHER
    if (mapping.cpt_code) return { type: 'CPT', code: mapping.cpt_code };
    if (mapping.hcpcs_code) return { type: 'HCPCS', code: mapping.hcpcs_code };
    if (mapping.drg_code) return { type: 'DRG', code: mapping.drg_code };
    if (mapping.revenue_code) return { type: 'REV', code: mapping.revenue_code };
    if (mapping.supply_code) return { type: 'SUPPLY', code: mapping.supply_code };
    if (mapping.drug_code || mapping.ndc_code) return { type: 'RX', code: mapping.drug_code || mapping.ndc_code };
    return { type: 'OTHER', code: mapping.chargemaster_code };
  };

  // Build analytics lookup from static JSON arrays — keyed by "type_code"
  // Contains national avg, markup %, hospital count for each procedure code per type
  const analyticsLookup = useMemo(() => {
    const lookup: Record<string, { na: number | null; mp: number | null; th: number | null; hc: number | null }> = {};
    if (!staticData) return lookup;
    const typeMap: Record<string, string> = {
      cpt: 'cpt', hcpcs: 'hcpcs', drg: 'drg', revenue: 'revenue', ndc: 'ndc', drug: 'drug', supply: 'supply'
    };
    for (const [k, prefix] of Object.entries(typeMap)) {
      const arr = (staticData as any)[k];
      if (!Array.isArray(arr)) continue;
      for (const e of arr) {
        if (e.c) {
          lookup[`${prefix}_${e.c}`] = { na: e.na, mp: e.mp, th: e.th, hc: e.hc };
        }
      }
    }
    return lookup;
  }, [staticData]);

  // Expand static mappings once (compact → full shape)
  const allStaticMappings = useMemo(() => {
    if (!hasStaticMappings) return [];
    return staticData!.mappings!.map((m, i) => expandMapping(m, i));
  }, [hasStaticMappings, staticData]);

  // Client-side filter/search/paginate static data
  const staticFiltered = useMemo(() => {
    if (!hasStaticMappings) return { items: [] as any[], total: 0 };

    let filtered = allStaticMappings;

    // Cleveland Clinic filter
    if (hospitalId === '4063e788-1130-4b32-98a0-8e09bbedd1b6') {
      filtered = filtered.filter(m => m.cpt_code !== 'Q2055');
    }

    // Search
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(m =>
        (m.procedure_description || '').toLowerCase().includes(lower) ||
        (m.chargemaster_description || '').toLowerCase().includes(lower) ||
        (m.cpt_code || '').toLowerCase().includes(lower) ||
        (m.drg_code || '').toLowerCase().includes(lower) ||
        (m.supply_code || '').toLowerCase().includes(lower) ||
        (m.drug_code || '').toLowerCase().includes(lower) ||
        (m.hcpcs_code || '').toLowerCase().includes(lower) ||
        (m.revenue_code || '').toLowerCase().includes(lower) ||
        (m.ndc_code || '').toLowerCase().includes(lower)
      );
    }

    // Code type filter — filter by whether mapping HAS this code type
    if (selectedCodeType) {
      switch (selectedCodeType) {
        case 'cpt': filtered = filtered.filter(m => m.cpt_code); break;
        case 'hcpcs': filtered = filtered.filter(m => m.hcpcs_code); break;
        case 'drg': filtered = filtered.filter(m => m.drg_code); break;
        case 'revenue': filtered = filtered.filter(m => m.revenue_code); break;
        case 'supply': filtered = filtered.filter(m => m.supply_code); break;
        case 'ndc': filtered = filtered.filter(m => m.ndc_code || m.drug_code); break;
        case 'other': filtered = filtered.filter(m =>
          !m.cpt_code && !m.hcpcs_code && !m.drg_code && !m.revenue_code && !m.supply_code && !m.ndc_code && !m.drug_code
        ); break;
      }
    }

    // Setting filter
    if (selectedSetting) {
      if (selectedSetting === 'unspecified') {
        filtered = filtered.filter(m => !m.inpatient_outpatient || m.inpatient_outpatient === '');
      } else {
        filtered = filtered.filter(m => m.inpatient_outpatient === selectedSetting);
      }
    }

    // Sort by markup % descending (worst price gaps first) when a category filter is active
    if (selectedCodeType) {
      const codeFieldMap: Record<string, string> = {
        cpt: 'cpt_code', hcpcs: 'hcpcs_code', drg: 'drg_code',
        revenue: 'revenue_code', ndc: 'ndc_code', supply: 'supply_code', drug: 'drug_code'
      };
      const analyticsPrefix: Record<string, string> = {
        cpt: 'cpt', hcpcs: 'hcpcs', drg: 'drg', revenue: 'revenue', ndc: 'ndc', supply: 'supply', drug: 'drug'
      };
      const field = codeFieldMap[selectedCodeType];
      const prefix = analyticsPrefix[selectedCodeType] || selectedCodeType;
      filtered.sort((a, b) => {
        const codeA = field ? (a[field] || '') : '';
        const codeB = field ? (b[field] || '') : '';
        const mpA = analyticsLookup[`${prefix}_${codeA}`]?.mp ?? -Infinity;
        const mpB = analyticsLookup[`${prefix}_${codeB}`]?.mp ?? -Infinity;
        return mpB - mpA;
      });
    }

    const total = filtered.length;
    const from = (currentPage - 1) * pageSize;
    const items = filtered.slice(from, from + pageSize);

    return { items, total };
  }, [allStaticMappings, hasStaticMappings, searchTerm, selectedCodeType, selectedSetting, currentPage, hospitalId]);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Fetch mappings: use static data if available, otherwise Supabase
  const { data: mappings = [], isLoading, isFetching } = useQuery({
    queryKey: ['hospital-mappings', hospitalId, searchTerm, currentPage, selectedCodeType, selectedSetting, hasStaticMappings],
    queryFn: async () => {
      // Use static data (client-side filtered)
      if (hasStaticMappings) {
        return staticFiltered.items;
      }

      // Supabase fallback
      let query = supabase
        .from('chargemaster_mappings')
        .select(`
          *,
          normalized_unit,
          normalized_price_per_unit
        `)
        .eq('hospital_id', hospitalId)
        .order('normalized_price_per_unit', { ascending: false, nullsFirst: false })
        .order('gross_charge', { ascending: false, nullsFirst: false })
        .order('averaged_price', { ascending: false, nullsFirst: false });

      if (hospitalId === '4063e788-1130-4b32-98a0-8e09bbedd1b6') {
        query = query.not('cpt_code', 'eq', 'Q2055');
      }

      if (searchTerm) {
        query = query.or(`procedure_description.ilike.%${searchTerm}%,chargemaster_description.ilike.%${searchTerm}%,cpt_code.ilike.%${searchTerm}%,drg_code.ilike.%${searchTerm}%,supply_code.ilike.%${searchTerm}%,drug_code.ilike.%${searchTerm}%`);
      }

      if (selectedCodeType) {
        switch (selectedCodeType) {
          case 'cpt': query = query.not('cpt_code', 'is', null); break;
          case 'hcpcs': query = query.not('hcpcs_code', 'is', null); break;
          case 'drg': query = query.not('drg_code', 'is', null); break;
          case 'revenue': query = query.not('revenue_code', 'is', null); break;
          case 'supply': query = query.not('supply_code', 'is', null); break;
          case 'ndc': query = query.not('ndc_code', 'is', null); break;
          case 'other':
            query = query.is('cpt_code', null).is('hcpcs_code', null).is('drg_code', null)
                         .is('revenue_code', null).is('supply_code', null).is('ndc_code', null);
            break;
        }
      }

      if (selectedSetting) {
        if (selectedSetting === 'unspecified') {
          query = query.or('inpatient_outpatient.is.null,inpatient_outpatient.eq.');
        } else {
          query = query.eq('inpatient_outpatient', selectedSetting);
        }
      }

      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: true,
    placeholderData: (previousData) => previousData
  });

  // Fetch consumer-friendly descriptions for procedures
  const { data: consumerFriendlyNames = {} } = useQuery({
    queryKey: ['consumer-friendly-names', mappings],
    queryFn: async () => {
      if (!mappings || mappings.length === 0) return {};

      // Collect only CPT/HCPCS codes — the `procedures` table is the CPT/HCPCS
      // namespace. REV and DRG codes share numeric formats with CPT (e.g. REV
      // 126 vs CPT 126 "Anesth tympanotomy"), so looking them up here pulls a
      // wrong description onto a revenue/DRG row. REV uses its chargemaster
      // description; DRG has its own drg_codes table.
      const procedureCodes = new Set<string>();
      mappings.forEach(mapping => {
        if (mapping.cpt_code) procedureCodes.add(mapping.cpt_code);
        if (mapping.hcpcs_code) procedureCodes.add(mapping.hcpcs_code);
      });

      if (procedureCodes.size === 0) return {};

      // Fetch consumer-friendly descriptions and code_status from procedures table
      const { data, error } = await supabase
        .from('procedures')
        .select('code, consumer_friendly_description, name, code_status, deletion_info')
        .in('code', Array.from(procedureCodes));

      if (error) throw error;

      // Create a map of code -> {name, code_status, deletion_info}
      const nameMap = {};
      data?.forEach(proc => {
        nameMap[proc.code] = {
          name: proc.consumer_friendly_description || proc.name,
          code_status: proc.code_status,
          deletion_info: proc.deletion_info
        };
      });

      return nameMap;
    },
    enabled: mappings.length > 0
  });

  // Total count: static data uses client-side count, otherwise Supabase
  const { data: totalCount = 0 } = useQuery({
    queryKey: ['hospital-mappings-count', hospitalId, searchTerm, selectedCodeType, selectedSetting, hasStaticMappings],
    queryFn: async () => {
      if (hasStaticMappings) {
        return staticFiltered.total;
      }

      let query = supabase
        .from('chargemaster_mappings')
        .select('*', { count: 'exact', head: true })
        .eq('hospital_id', hospitalId);

      if (hospitalId === '4063e788-1130-4b32-98a0-8e09bbedd1b6') {
        query = query.not('cpt_code', 'eq', 'Q2055');
      }
      if (searchTerm) {
        query = query.or(`procedure_description.ilike.%${searchTerm}%,chargemaster_description.ilike.%${searchTerm}%,cpt_code.ilike.%${searchTerm}%,drg_code.ilike.%${searchTerm}%,supply_code.ilike.%${searchTerm}%,drug_code.ilike.%${searchTerm}%`);
      }
      if (selectedCodeType) {
        switch (selectedCodeType) {
          case 'cpt': query = query.not('cpt_code', 'is', null); break;
          case 'hcpcs': query = query.not('hcpcs_code', 'is', null); break;
          case 'drg': query = query.not('drg_code', 'is', null); break;
          case 'revenue': query = query.not('revenue_code', 'is', null); break;
          case 'supply': query = query.not('supply_code', 'is', null); break;
          case 'ndc': query = query.not('ndc_code', 'is', null); break;
          case 'other':
            query = query.is('cpt_code', null).is('hcpcs_code', null).is('drg_code', null)
                         .is('revenue_code', null).is('supply_code', null).is('ndc_code', null);
            break;
        }
      }
      if (selectedSetting) {
        if (selectedSetting === 'unspecified') {
          query = query.or('inpatient_outpatient.is.null,inpatient_outpatient.eq.');
        } else {
          query = query.eq('inpatient_outpatient', selectedSetting);
        }
      }

      const { count, error } = await query;
      if (error) throw error;
      return count || 0;
    },
    enabled: true
  });

  // Compute actual code type counts from mappings (for filter buttons)
  // Shows "analyzed_count / chargemaster_total" for each code type
  const mappingCounts = useMemo(() => {
    const counts: Record<string, number> = {
      cpt: 0, hcpcs: 0, drg: 0, rev: 0, ndc: 0, supply: 0, drug: 0, uncoded: 0
    };
    const typeToKey: Record<string, string> = {
      CPT: 'cpt', HCPCS: 'hcpcs', DRG: 'drg', REV: 'rev',
      RX: 'ndc', SUPPLY: 'supply', OTHER: 'uncoded'
    };
    if (!hasStaticMappings) return counts;
    for (const m of allStaticMappings) {
      const { type } = getCodeType(m);
      const key = typeToKey[type] || 'uncoded';
      counts[key]++;
    }
    return counts;
  }, [hasStaticMappings, allStaticMappings]);

  // Count from analytics arrays: ranked (has national avg) / total per code type
  // Analytics track each code type independently (a row with CPT+REV counts in both)
  const analyticsCounts = useMemo(() => {
    const result: Record<string, { ranked: number; total: number }> = {};
    for (const k of ['cpt', 'hcpcs', 'drg', 'revenue', 'ndc', 'drug', 'supply'] as const) {
      const arr = staticData?.[k];
      if (Array.isArray(arr) && arr.length > 0) {
        result[k] = {
          total: arr.length,
          ranked: arr.filter((e: any) => e.na != null).length,
        };
      }
    }
    return result;
  }, [staticData]);

  // Build effective data for filter buttons — "ranked / total" from analytics
  const effectiveRankingData = useMemo(() => {
    const ac = analyticsCounts;
    return {
      cpt_procedure_count: ac.cpt?.ranked ?? 0,
      cpt_total_available: ac.cpt?.total ?? 0,
      hcpcs_procedure_count: ac.hcpcs?.ranked ?? 0,
      hcpcs_total_available: ac.hcpcs?.total ?? 0,
      drg_procedure_count: ac.drg?.ranked ?? 0,
      drg_total_available: ac.drg?.total ?? 0,
      rev_procedure_count: ac.revenue?.ranked ?? 0,
      rev_total_available: ac.revenue?.total ?? 0,
      ndc_procedure_count: ac.ndc?.ranked ?? 0,
      ndc_total_available: ac.ndc?.total ?? 0,
      supply_procedure_count: ac.supply?.ranked ?? 0,
      supply_total_available: ac.supply?.total ?? 0,
      drug_procedure_count: ac.drug?.ranked ?? 0,
      drug_total_available: ac.drug?.total ?? 0,
      uncoded_procedures: mappingCounts?.uncoded ?? 0,
    };
  }, [analyticsCounts, mappingCounts]);


  const handleCodeTypeFilter = (codeType: string) => {
    setSelectedCodeType(selectedCodeType === codeType ? null : codeType);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSettingFilter = (setting: string) => {
    setSelectedSetting(selectedSetting === setting ? null : setting);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Get total count without filters to check if hospital truly has no data
  const { data: unfilteredCount = 0 } = useQuery({
    queryKey: ['hospital-mappings-total', hospitalId, hasStaticMappings],
    queryFn: async () => {
      if (hasStaticMappings) {
        return allStaticMappings.length;
      }

      const { count, error } = await supabase
        .from('chargemaster_mappings')
        .select('*', { count: 'exact', head: true })
        .eq('hospital_id', hospitalId);

      if (error) throw error;
      return count || 0;
    },
    enabled: true
  });

  // Fetch summary stats: static first, Supabase fallback
  const { data: summaryStats } = useQuery({
    queryKey: ['hospital-summary-stats', hospitalId, hasStaticMappings],
    queryFn: async () => {
      if (staticData?.mappings_meta) {
        return {
          totalCount: staticData.mappings_meta.total || 0,
          avgPrice: staticData.mappings_meta.avg_gross || 0,
          maxPrice: staticData.mappings_meta.max_gross || 0,
        };
      }

      const { data, error } = await supabase
        .rpc('calculate_hospital_averages', { p_hospital_id: hospitalId });

      if (error) throw error;

      if (!data || data.length === 0) {
        return { totalCount: 0, avgPrice: 0, maxPrice: 0 };
      }

      const result = data[0];
      return {
        totalCount: Number(result.total_count) || 0,
        avgPrice: Number(result.avg_gross_charge) || 0,
        maxPrice: Number(result.max_gross_charge) || 0
      };
    },
    enabled: true
  });

  // Fetch national averages for displayed procedures from specific pricing tables
  const { data: nationalAverages } = useQuery({
    queryKey: ['national-averages', mappings.map(m => m.id).join(',')],
    queryFn: async () => {
      if (!mappings.length) return {};

      // Group mappings by code type for efficient batch queries
      const cptMappings = mappings.filter(m => m.cpt_code);
      const hcpcsMappings = mappings.filter(m => m.hcpcs_code);
      const drgMappings = mappings.filter(m => m.drg_code);
      const revMappings = mappings.filter(m => m.revenue_code);
      const ndcMappings = mappings.filter(m => m.ndc_code || m.drug_code);

      const lookup: Record<string, any> = {};

      // Fetch CPT averages
      if (cptMappings.length > 0) {
        const cptCodes = [...new Set(cptMappings.map(m => m.cpt_code))];
        const { data: cptData, error: cptError } = await supabase
          .from('cpt_pricing_averages')
          .select('cpt_code, setting_type, normalized_unit, unit_quantity, avg_gross_charge, median_gross_charge, avg_discounted_cash, total_hospitals, total_records')
          .in('cpt_code', cptCodes);

        if (!cptError && cptData) {
          cptData.forEach(avg => {
            // Create key matching mapping fields - use ?? for proper null handling
            const settingType = avg.setting_type ?? 'unspecified';
            const normalizedUnit = avg.normalized_unit ?? 'unspecified';
            const unitQuantity = avg.unit_quantity ?? 1;
            const key = `CPT_${avg.cpt_code}_${settingType}_${normalizedUnit}_${unitQuantity}`;
            lookup[key] = { ...avg, code_type: 'CPT' };
          });
        }
      }

      // Fetch HCPCS averages
      if (hcpcsMappings.length > 0) {
        const hcpcsCodes = [...new Set(hcpcsMappings.map(m => m.hcpcs_code))];
        const { data: hcpcsData, error: hcpcsError } = await supabase
          .from('hcpcs_pricing_averages')
          .select('hcpcs_code, setting_type, normalized_unit, unit_quantity, avg_gross_charge, median_gross_charge, avg_discounted_cash, total_hospitals, total_records')
          .in('hcpcs_code', hcpcsCodes);

        if (!hcpcsError && hcpcsData) {
          hcpcsData.forEach(avg => {
            // Create key matching mapping fields - use ?? for proper null handling
            const settingType = avg.setting_type ?? 'unspecified';
            const normalizedUnit = avg.normalized_unit ?? 'unspecified';
            const unitQuantity = avg.unit_quantity ?? 1;
            const key = `HCPCS_${avg.hcpcs_code}_${settingType}_${normalizedUnit}_${unitQuantity}`;
            lookup[key] = { ...avg, code_type: 'HCPCS' };
          });
        }
      }

      // Fetch DRG averages
      if (drgMappings.length > 0) {
        const drgCodes = [...new Set(drgMappings.map(m => m.drg_code))];

        // Extract numeric codes from chargemaster mappings for comparison
        // "MS-003" -> "003", "MS-360" -> "360", "003" -> "003"
        const numericDrgCodes = drgCodes.map(code => {
          const match = code.match(/\d+/);
          return match ? match[0] : code;
        });

        // Fetch ALL DRG averages since we need to match by extracting numeric codes
        const { data: drgData, error: drgError } = await supabase
          .from('drg_pricing_averages')
          .select('drg_code, avg_gross_charge, median_gross_charge, avg_discounted_cash, total_hospitals, total_records');

        if (!drgError && drgData) {
          drgData.forEach(avg => {
            // Extract numeric code from different formats:
            // "MS-346" -> "346"
            // "MS-DRG V40 (FY 2023) 459" -> "459"
            let numericCode: string | null = null;

            // Try MS-### format first
            const msMatch = avg.drg_code.match(/^MS-(\d+)$/);
            if (msMatch) {
              numericCode = msMatch[1];
            } else {
              // Try long format: extract trailing number
              const longMatch = avg.drg_code.match(/\d+$/);
              if (longMatch) {
                numericCode = longMatch[0];
              }
            }

            if (numericCode && numericDrgCodes.includes(numericCode)) {
              const normalizedUnit = avg.normalized_unit ?? 'unspecified';
              const unitQuantity = avg.unit_quantity ?? 1;
              // Use the numeric code as the key to match hospital mappings
              const key = `DRG_${numericCode}_unspecified_${normalizedUnit}_${unitQuantity}`;
              lookup[key] = { ...avg, code_type: 'DRG', numeric_code: numericCode };
            }
          });
        }
      }

      // Fetch Revenue averages
      if (revMappings.length > 0) {
        const revCodes = [...new Set(revMappings.map(m => m.revenue_code))];
        const { data: revData, error: revError } = await supabase
          .from('revenue_pricing_averages')
          .select('revenue_code, setting_type, normalized_unit, unit_quantity, avg_charge, median_charge, total_hospitals')
          .in('revenue_code', revCodes);

        if (!revError && revData) {
          revData.forEach(avg => {
            const settingType = avg.setting_type ?? 'unspecified';
            const normalizedUnit = avg.normalized_unit ?? 'unspecified';
            const unitQuantity = avg.unit_quantity ?? 1;
            const key = `REV_${avg.revenue_code}_${settingType}_${normalizedUnit}_${unitQuantity}`;
            // Map avg_charge to avg_gross_charge for consistency
            lookup[key] = {
              ...avg,
              avg_gross_charge: avg.avg_charge,
              median_gross_charge: avg.median_charge,
              code_type: 'REV'
            };
          });
        }
      }

      // Fetch NDC averages
      if (ndcMappings.length > 0) {
        const ndcCodes = [...new Set(ndcMappings.map(m => m.ndc_code || m.drug_code).filter(Boolean))];
        const { data: ndcData, error: ndcError } = await supabase
          .from('ndc_pricing_averages')
          .select('ndc_code, ndc_name, avg_charge, median_charge, total_hospitals')
          .in('ndc_code', ndcCodes);

        if (!ndcError && ndcData) {
          ndcData.forEach(avg => {
            // NDC pricing averages don't have setting_type/normalized_unit columns
            // Use simplified key: RX_${code}_unspecified_1 (no setting_type, no normalized_unit)
            const key = `RX_${avg.ndc_code}_unspecified_1`;
            lookup[key] = {
              ...avg,
              code_type: 'RX',
              // Map column names to match expected format
              avg_gross_charge: avg.avg_charge,
              median_gross_charge: avg.median_charge
            };
          });
        }
      }

      return lookup;
    },
    enabled: mappings.length > 0
  });

  // Function to get national average for a specific procedure
  const getNationalAverage = (mapping: any) => {
    // When a code type filter is active, prefer analytics data from static JSON
    // This gives the correct per-code-type comparison (e.g., NDC avg for RX filter)
    if (selectedCodeType) {
      const codeFieldMap: Record<string, string> = {
        cpt: 'cpt_code', hcpcs: 'hcpcs_code', drg: 'drg_code',
        revenue: 'revenue_code', ndc: 'ndc_code', supply: 'supply_code', drug: 'drug_code'
      };
      const analyticsPrefix: Record<string, string> = {
        cpt: 'cpt', hcpcs: 'hcpcs', drg: 'drg', revenue: 'revenue', ndc: 'ndc', supply: 'supply', drug: 'drug'
      };
      const field = codeFieldMap[selectedCodeType];
      const prefix = analyticsPrefix[selectedCodeType];
      if (field && prefix && mapping[field]) {
        const entry = analyticsLookup[`${prefix}_${mapping[field]}`];
        if (entry && entry.na != null) {
          return {
            avg_gross_charge: entry.na,
            total_hospitals: entry.th,
            matched_setting: mapping.inpatient_outpatient?.toLowerCase() || 'unspecified',
          };
        }
      }
    }

    if (!nationalAverages) return null;

    const codeInfo = getCodeType(mapping);
    const codeType = codeInfo.type;
    let code = codeInfo.code;

    // For DRG codes, extract numeric portion to match with averages
    // "MS-003" -> "003", "APR-123" -> "123", "003" -> "003"
    if (codeType === 'DRG') {
      const numericMatch = code.match(/\d+/);
      if (numericMatch) {
        code = numericMatch[0];
      }
    }

    // Get setting type - CPT and REV use inpatient_outpatient, DRG doesn't have it
    // Normalize to lowercase to match pricing_averages tables (some hospitals use "Inpatient"/"Outpatient")
    const settingType = mapping.inpatient_outpatient?.toLowerCase() ?? 'unspecified';

    // Get normalization fields - use ?? for proper null/undefined handling
    const normalizedUnit = mapping.normalized_unit ?? 'unspecified';
    const unitQuantity = mapping.unit_quantity ?? 1;

    // If hospital specifies 'both', intentionally average across all settings
    // (procedure can be performed in multiple settings)
    if (settingType === 'both' && (codeType === 'CPT' || codeType === 'HCPCS' || codeType === 'REV')) {
      const settingTypesToAverage = ['inpatient', 'outpatient', 'both', 'unspecified'];
      const matchingRecords = [];

      for (const trySettingType of settingTypesToAverage) {
        const key = `${codeType}_${code}_${trySettingType}_${normalizedUnit}_${unitQuantity}`;
        const record = nationalAverages[key];
        if (record) {
          matchingRecords.push(record);
        }
      }

      // If we found records across different settings, average them
      if (matchingRecords.length > 0) {
        const avgGrossCharge = matchingRecords.reduce((sum, r) => sum + (r.avg_gross_charge || 0), 0) / matchingRecords.length;
        const avgDiscountedCash = matchingRecords.reduce((sum, r) => sum + (r.avg_discounted_cash || 0), 0) / matchingRecords.length;
        const totalHospitals = Math.max(...matchingRecords.map(r => r.total_hospitals || 0));
        const totalRecords = matchingRecords.reduce((sum, r) => sum + (r.total_records || 0), 0);

        return {
          avg_gross_charge: avgGrossCharge,
          avg_discounted_cash: avgDiscountedCash,
          total_hospitals: totalHospitals,
          total_records: totalRecords,
          code_type: codeType,
          matched_setting: 'both',
          settings_averaged: matchingRecords.length
        };
      }
    }

    // Build lookup key matching the format used when creating the lookup
    let key: string;
    if (codeType === 'RX') {
      // RX (NDC) codes don't have setting_type or normalized_unit - use simplified key
      key = `${codeType}_${code}_unspecified_1`;
    } else if (codeType === 'DRG') {
      // DRG codes don't have setting_type but do have normalized_unit
      key = `${codeType}_${code}_unspecified_${normalizedUnit}_${unitQuantity}`;
    } else {
      key = `${codeType}_${code}_${settingType}_${normalizedUnit}_${unitQuantity}`;
    }

    // Try exact match first
    let result = nationalAverages[key];
    if (result) {
      return { ...result, matched_setting: settingType };
    }

    // If no exact match for CPT/HCPCS/REV, calculate average across ALL settings (fallback)
    if (!result && (codeType === 'CPT' || codeType === 'HCPCS' || codeType === 'REV')) {
      const settingTypesToAverage = ['inpatient', 'outpatient', 'both', 'unspecified'];
      const matchingRecords = [];

      for (const trySettingType of settingTypesToAverage) {
        key = `${codeType}_${code}_${trySettingType}_${normalizedUnit}_${unitQuantity}`;
        const record = nationalAverages[key];
        if (record) {
          matchingRecords.push(record);
        }
      }

      // If we found records across different settings, average them
      if (matchingRecords.length > 0) {
        const avgGrossCharge = matchingRecords.reduce((sum, r) => sum + (r.avg_gross_charge || 0), 0) / matchingRecords.length;
        const avgDiscountedCash = matchingRecords.reduce((sum, r) => sum + (r.avg_discounted_cash || 0), 0) / matchingRecords.length;
        const totalHospitals = Math.max(...matchingRecords.map(r => r.total_hospitals || 0));
        const totalRecords = matchingRecords.reduce((sum, r) => sum + (r.total_records || 0), 0);

        return {
          avg_gross_charge: avgGrossCharge,
          avg_discounted_cash: avgDiscountedCash,
          total_hospitals: totalHospitals,
          total_records: totalRecords,
          code_type: codeType,
          matched_setting: 'all_settings',
          settings_averaged: matchingRecords.length
        };
      }
    }

    // Fallback to unspecified normalized_unit
    if (!result) {
      if (codeType === 'DRG') {
        key = `${codeType}_${code}_unspecified_unspecified_1`;
      } else {
        key = `${codeType}_${code}_${settingType}_unspecified_1`;
      }
      result = nationalAverages[key];
      if (result) {
        return { ...result, matched_setting: settingType };
      }
    }

    // Last resort: try unspecified for everything
    if (!result && (codeType === 'CPT' || codeType === 'HCPCS' || codeType === 'REV')) {
      key = `${codeType}_${code}_unspecified_unspecified_1`;
      result = nationalAverages[key];
      if (result) {
        return { ...result, matched_setting: 'unspecified' };
      }
    }

    return null;
  };

  // Function to calculate percentage difference
  const calculatePercentageDifference = (hospitalPrice: number, nationalAvg: number) => {
    if (!hospitalPrice || !nationalAvg) return null;
    return ((hospitalPrice - nationalAvg) / nationalAvg) * 100;
  };

  // Function to format percentage with appropriate styling
  const formatPercentage = (percentage: number | null) => {
    if (percentage === null) return "—";
    const sign = percentage >= 0 ? "+" : "";
    const colorClass = percentage >= 0 ? "text-red-500" : "text-green-500";
    return <span className={`${colorClass} font-mono font-bold text-lg`}>{sign}{percentage.toFixed(1)}%</span>;
  };


  const formatCurrency = (amount: number | null) => {
    if (!amount) return "—";
    return `$${amount.toLocaleString()}`;
  };

  // Only show "No Pricing Data" if hospital truly has no data at all
  if (unfilteredCount === 0 && !isLoading) {
    return (
      <Card className="terminal-card p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold mb-2">{t('hospital:mappingsTable.noPricingData.title')}</h3>
        <p className="text-muted-foreground">
          {t('hospital:mappingsTable.noPricingData.description')}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Consumer summary: services listed, cash discount, common-care price check */}
      <HospitalPriceSummary
        staticData={staticData}
        totalListed={summaryStats?.totalCount || 0}
        loading={staticLoading}
        onPickCode={(code, family) => {
          setSelectedCodeType(family === 'drg' ? 'drg' : 'cpt');
          setSelectedSetting(null);
          setSearchInput(code);
          setCurrentPage(1);
          document.getElementById('hospital-price-list-search')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      />

      {/* Code Type Breakdown & Setting Filter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Code Type Breakdown */}
        <Card className="terminal-card p-6">
          <h3 className="text-lg font-semibold mb-4">{t('hospital:mappingsTable.codeBreakdown.title')}</h3>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => handleCodeTypeFilter('cpt')}
              disabled={(effectiveRankingData?.cpt_total_available || 0) === 0}
              className={`px-4 py-2 rounded font-mono font-bold transition-all ${
                selectedCodeType === 'cpt'
                  ? 'scale-105'
                  : ''
              }`}
              style={{
                backgroundColor: (effectiveRankingData?.cpt_total_available || 0) === 0 ? disabledBg : badgeBg,
                borderColor: '#60a5fa',
                borderWidth: selectedCodeType === 'cpt' ? '2px' : '1px',
                borderStyle: 'solid',
                color: (effectiveRankingData?.cpt_total_available || 0) === 0 ? disabledText : '#60a5fa',
                opacity: (effectiveRankingData?.cpt_total_available || 0) === 0 ? 0.5 : 1,
                cursor: (effectiveRankingData?.cpt_total_available || 0) === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              CPT: {(effectiveRankingData?.cpt_procedure_count || 0).toLocaleString()}/{(effectiveRankingData?.cpt_total_available || 0).toLocaleString()}
            </button>
            <button
              onClick={() => handleCodeTypeFilter('drg')}
              disabled={(effectiveRankingData?.drg_total_available || 0) === 0}
              className={`px-4 py-2 rounded font-mono font-bold transition-all ${
                selectedCodeType === 'drg'
                  ? 'scale-105'
                  : ''
              }`}
              style={{
                backgroundColor: (effectiveRankingData?.drg_total_available || 0) === 0 ? disabledBg : badgeBg,
                borderColor: '#c084fc',
                borderWidth: selectedCodeType === 'drg' ? '2px' : '1px',
                borderStyle: 'solid',
                color: (effectiveRankingData?.drg_total_available || 0) === 0 ? disabledText : '#c084fc',
                opacity: (effectiveRankingData?.drg_total_available || 0) === 0 ? 0.5 : 1,
                cursor: (effectiveRankingData?.drg_total_available || 0) === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              DRG: {(effectiveRankingData?.drg_procedure_count || 0).toLocaleString()}/{(effectiveRankingData?.drg_total_available || 0).toLocaleString()}
            </button>
            <button
              onClick={() => handleCodeTypeFilter('revenue')}
              disabled={(effectiveRankingData?.rev_total_available || 0) === 0}
              className={`px-4 py-2 rounded font-mono font-bold transition-all ${
                selectedCodeType === 'revenue'
                  ? 'scale-105'
                  : ''
              }`}
              style={{
                backgroundColor: (effectiveRankingData?.rev_total_available || 0) === 0 ? disabledBg : badgeBg,
                borderColor: '#facc15',
                borderWidth: selectedCodeType === 'revenue' ? '2px' : '1px',
                borderStyle: 'solid',
                color: (effectiveRankingData?.rev_total_available || 0) === 0 ? disabledText : '#facc15',
                opacity: (effectiveRankingData?.rev_total_available || 0) === 0 ? 0.5 : 1,
                cursor: (effectiveRankingData?.rev_total_available || 0) === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              Revenue: {(effectiveRankingData?.rev_procedure_count || 0).toLocaleString()}/{(effectiveRankingData?.rev_total_available || 0).toLocaleString()}
            </button>
            <button
              onClick={() => handleCodeTypeFilter('supply')}
              disabled={(effectiveRankingData?.supply_total_available || 0) === 0}
              className={`px-4 py-2 rounded font-mono font-bold transition-all ${
                selectedCodeType === 'supply'
                  ? 'scale-105'
                  : ''
              }`}
              style={{
                backgroundColor: (effectiveRankingData?.supply_total_available || 0) === 0 ? disabledBg : badgeBg,
                borderColor: '#fb923c',
                borderWidth: selectedCodeType === 'supply' ? '2px' : '1px',
                borderStyle: 'solid',
                color: (effectiveRankingData?.supply_total_available || 0) === 0 ? disabledText : '#fb923c',
                opacity: (effectiveRankingData?.supply_total_available || 0) === 0 ? 0.5 : 1,
                cursor: (effectiveRankingData?.supply_total_available || 0) === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              SUP: {(effectiveRankingData?.supply_procedure_count || 0).toLocaleString()}/{(effectiveRankingData?.supply_total_available || 0).toLocaleString()}
            </button>
            <button
              onClick={() => handleCodeTypeFilter('hcpcs')}
              disabled={(effectiveRankingData?.hcpcs_total_available || 0) === 0}
              className={`px-4 py-2 rounded font-mono font-bold transition-all ${
                selectedCodeType === 'hcpcs'
                  ? 'scale-105'
                  : ''
              }`}
              style={{
                backgroundColor: (effectiveRankingData?.hcpcs_total_available || 0) === 0 ? disabledBg : badgeBg,
                borderColor: '#4ade80',
                borderWidth: selectedCodeType === 'hcpcs' ? '2px' : '1px',
                borderStyle: 'solid',
                color: (effectiveRankingData?.hcpcs_total_available || 0) === 0 ? disabledText : '#4ade80',
                opacity: (effectiveRankingData?.hcpcs_total_available || 0) === 0 ? 0.5 : 1,
                cursor: (effectiveRankingData?.hcpcs_total_available || 0) === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              HCPCS: {(effectiveRankingData?.hcpcs_procedure_count || 0).toLocaleString()}/{(effectiveRankingData?.hcpcs_total_available || 0).toLocaleString()}
            </button>
            <button
              onClick={() => handleCodeTypeFilter('ndc')}
              disabled={(effectiveRankingData?.ndc_total_available || 0) === 0}
              className={`px-4 py-2 rounded font-mono font-bold transition-all ${
                selectedCodeType === 'ndc'
                  ? 'scale-105'
                  : ''
              }`}
              style={{
                backgroundColor: (effectiveRankingData?.ndc_total_available || 0) === 0 ? disabledBg : badgeBg,
                borderColor: '#fb7185',
                borderWidth: selectedCodeType === 'ndc' ? '2px' : '1px',
                borderStyle: 'solid',
                color: (effectiveRankingData?.ndc_total_available || 0) === 0 ? disabledText : '#fb7185',
                opacity: (effectiveRankingData?.ndc_total_available || 0) === 0 ? 0.5 : 1,
                cursor: (effectiveRankingData?.ndc_total_available || 0) === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              RX: {(effectiveRankingData?.ndc_procedure_count || 0).toLocaleString()}/{(effectiveRankingData?.ndc_total_available || 0).toLocaleString()}
            </button>
            <button
              onClick={() => handleCodeTypeFilter('other')}
              disabled={(effectiveRankingData?.uncoded_procedures || 0) === 0}
              className={`px-4 py-2 rounded font-mono font-bold transition-all ${
                selectedCodeType === 'other'
                  ? 'scale-105'
                  : ''
              }`}
              style={{
                backgroundColor: (effectiveRankingData?.uncoded_procedures || 0) === 0 ? disabledBg : badgeBg,
                borderColor: '#9ca3af',
                borderWidth: selectedCodeType === 'other' ? '2px' : '1px',
                borderStyle: 'solid',
                color: (effectiveRankingData?.uncoded_procedures || 0) === 0 ? disabledText : '#9ca3af',
                opacity: (effectiveRankingData?.uncoded_procedures || 0) === 0 ? 0.5 : 1,
                cursor: (effectiveRankingData?.uncoded_procedures || 0) === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              CDM: {(effectiveRankingData?.uncoded_procedures || 0).toLocaleString()}
            </button>
          </div>
          {(selectedCodeType || selectedSetting) && (
            <div className="mt-3">
              <button
                onClick={() => {
                  setSelectedCodeType(null);
                  setSelectedSetting(null);
                }}
                className="text-sm text-muted-foreground hover:text-foreground underline"
              >
                {t('hospital:mappingsTable.codeBreakdown.clearAllFilters')}
              </button>
            </div>
          )}
        </Card>

        {/* Setting Type Filter */}
        <Card className="terminal-card p-6">
          <h3 className="text-lg font-semibold mb-4">{t('hospital:mappingsTable.settingFilter.title')}</h3>
          <div className="flex flex-wrap gap-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleSettingFilter('inpatient')}
                    className={`px-4 py-2 rounded border transition-colors ${
                      selectedSetting === 'inpatient'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-border hover:bg-secondary'
                    }`}
                  >
                    🏥 {t('hospital:mappingsTable.settingFilter.inpatient')}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-xs">
                    <p className="font-semibold">🏥 {t('hospital:mappingsTable.settingTooltips.inpatientTitle')}</p>
                    <p className="text-xs">{t('hospital:mappingsTable.settingTooltips.inpatientDescription')}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleSettingFilter('outpatient')}
                    className={`px-4 py-2 rounded border transition-colors ${
                      selectedSetting === 'outpatient'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-border hover:bg-secondary'
                    }`}
                  >
                    🚶 {t('hospital:mappingsTable.settingFilter.outpatient')}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-xs">
                    <p className="font-semibold">🚶 {t('hospital:mappingsTable.settingTooltips.outpatientTitle')}</p>
                    <p className="text-xs">{t('hospital:mappingsTable.settingTooltips.outpatientDescription')}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleSettingFilter('both')}
                    className={`px-4 py-2 rounded border transition-colors ${
                      selectedSetting === 'both'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-border hover:bg-secondary'
                    }`}
                  >
                    🏥🚶 {t('hospital:mappingsTable.settingFilter.both')}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-xs">
                    <p className="font-semibold">🏥🚶 {t('hospital:mappingsTable.settingTooltips.bothTitle')}</p>
                    <p className="text-xs">{t('hospital:mappingsTable.settingTooltips.bothDescription')}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleSettingFilter('unspecified')}
                    className={`px-4 py-2 rounded border transition-colors ${
                      selectedSetting === 'unspecified'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-border hover:bg-secondary'
                    }`}
                  >
                    ❓ {t('hospital:mappingsTable.settingFilter.unspecified')}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-xs">
                    <p className="font-semibold">❓ {t('hospital:mappingsTable.settingTooltips.unspecifiedTitle')}</p>
                    <p className="text-xs">{t('hospital:mappingsTable.settingTooltips.unspecifiedDescription')}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {selectedSetting && (
            <div className="mt-3">
              <button
                onClick={() => setSelectedSetting(null)}
                className="text-sm text-muted-foreground hover:text-foreground underline"
              >
                {t('hospital:mappingsTable.settingFilter.clearSettingFilter')}
              </button>
            </div>
          )}
        </Card>
      </div>

      {/* Search */}
      <div className="relative scroll-mt-24" id="hospital-price-list-search">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder={t('hospital:mappingsTable.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-9 bg-background border-border text-foreground placeholder:text-muted-foreground focus:ring-ring focus:border-ring"
        />
      </div>

      {/* Loading Indicator */}
      {isFetching && !isLoading && (
        <div className="flex items-center justify-center gap-2 py-3 bg-muted/30 rounded-lg">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          <span className="text-sm text-muted-foreground">{t('hospital:mappingsTable.updatingResults')}</span>
        </div>
      )}

      {/* Mappings Table */}
      <Card className="terminal-card relative">
        <div className="overflow-x-auto">
          {(() => { const showNdcColumn = mappings.some((m: any) => m.ndc_code); return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={`${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'} font-bold w-12`}>{t('hospital:mappingsTable.headers.row')}</TableHead>
                <TableHead className={`${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'} font-bold`}>{t('hospital:mappingsTable.headers.code')}</TableHead>
                <TableHead className={isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'}>{t('hospital:mappingsTable.headers.type')}</TableHead>
                {showNdcColumn && <TableHead className={`${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'} font-mono text-xs`}>{t('hospital:mappingsTable.headers.ndc')}</TableHead>}
                <TableHead className={isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'}>{t('hospital:mappingsTable.headers.description')}</TableHead>
                <TableHead className={`text-center ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'}`}>{t('hospital:mappingsTable.headers.setting')}</TableHead>
                <TableHead className={`text-right ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'}`}>
                  <div className="flex items-center justify-end gap-1">
                    {t('hospital:mappingsTable.headers.hospitalCharge')}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-xs max-w-xs">
                            <p>{t('hospital:mappingsTable.chargeTooltip.description')}</p>
                            <p className="Mt-1">{t('hospital:mappingsTable.chargeTooltip.averaged')}</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </TableHead>
                <TableHead className={`text-right ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'}`}>{t('hospital:mappingsTable.headers.nationalAvg')}</TableHead>
                <TableHead className={`text-right ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'}`}>{t('hospital:mappingsTable.headers.difference')}</TableHead>
                <TableHead className={`text-center ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'}`}>{t('hospital:mappingsTable.headers.compare')}</TableHead>
                <TableHead className={`text-right ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'}`}>{t('hospital:mappingsTable.headers.cashPrice')}</TableHead>
                <TableHead className={`text-right ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'}`}>{t('hospital:mappingsTable.headers.minRate')}</TableHead>
                <TableHead className={`text-right ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'}`}>{t('hospital:mappingsTable.headers.maxRate')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                  </TableRow>
                ))
              ) : mappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <AlertTriangle className="w-8 h-8 text-muted-foreground" />
                      <div>
                        <p className="text-lg font-semibold text-foreground">{t('hospital:mappingsTable.noResults.title')}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {(selectedCodeType || selectedSetting || searchTerm)
                            ? t('hospital:mappingsTable.noResults.withFilters')
                            : t('hospital:mappingsTable.noResults.noData')}
                        </p>
                      </div>
                      {(selectedCodeType || selectedSetting || searchTerm) && (
                        <button
                          onClick={() => {
                            setSelectedCodeType(null);
                            setSelectedSetting(null);
                            setSearchInput('');
                          }}
                          className="text-sm text-primary hover:underline"
                        >
                          {t('hospital:mappingsTable.codeBreakdown.clearAllFilters')}
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                mappings.map((mapping, index) => {
                  // When a filter is active, show that code type instead of priority-based type
                  const codeInfo = selectedCodeType
                    ? (() => {
                        const filterMap: Record<string, { type: string; codeField: string }> = {
                          cpt: { type: 'CPT', codeField: 'cpt_code' },
                          hcpcs: { type: 'HCPCS', codeField: 'hcpcs_code' },
                          drg: { type: 'DRG', codeField: 'drg_code' },
                          revenue: { type: 'REV', codeField: 'revenue_code' },
                          supply: { type: 'SUPPLY', codeField: 'supply_code' },
                          ndc: { type: 'RX', codeField: 'ndc_code' },
                        };
                        const fm = filterMap[selectedCodeType];
                        if (fm && mapping[fm.codeField]) {
                          return { type: fm.type, code: mapping[fm.codeField] };
                        }
                        return getCodeType(mapping);
                      })()
                    : getCodeType(mapping);
                  const nationalAvg = getNationalAverage(mapping);
                  const percentageDiff = calculatePercentageDifference(mapping.gross_charge, nationalAvg?.avg_gross_charge);
                  const rowNumber = (currentPage - 1) * pageSize + index + 1;
                  
                  return (
                    <TableRow key={mapping.id}>
                      <TableCell className="text-center font-mono font-bold text-muted-foreground">
                        {rowNumber}
                      </TableCell>
                      <TableCell className="font-mono text-terminal-green">
                        <div className="flex items-center gap-2">
                          {codeInfo.code}
                          {(() => {
                            // Only CPT/HCPCS resolve against the procedures table — REV/DRG
                            // numeric codes collide with CPT and must not look up here.
                            const procedureCode = mapping.cpt_code || mapping.hcpcs_code;
                            const procInfo = procedureCode ? consumerFriendlyNames[procedureCode] : null;
                            if (procInfo && procInfo.code_status && procInfo.code_status !== 'active') {
                              return (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge
                                        variant="outline"
                                        className="text-xs bg-orange-500/10 text-orange-600 border-orange-500/20 cursor-help"
                                      >
                                        {t('hospital:mappingsTable.excluded')}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      <p className="font-semibold">{t('hospital:mappingsTable.excludedDescription')}</p>
                                      <p className="text-sm mt-1">{procInfo.deletion_info || t('hospital:mappingsTable.excludedDefault')}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className="px-3 py-1 rounded font-mono font-bold text-xs inline-block"
                          style={{
                            backgroundColor: badgeBg,
                            borderColor: codeInfo.type === 'CPT' ? '#60a5fa' :
                                        codeInfo.type === 'HCPCS' ? '#4ade80' :
                                        codeInfo.type === 'DRG' ? '#c084fc' :
                                        codeInfo.type === 'REV' ? '#facc15' :
                                        codeInfo.type === 'SUPPLY' ? '#fb923c' :
                                        codeInfo.type === 'RX' ? '#fb7185' :
                                        '#9ca3af',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            color: codeInfo.type === 'CPT' ? '#60a5fa' :
                                  codeInfo.type === 'HCPCS' ? '#4ade80' :
                                  codeInfo.type === 'DRG' ? '#c084fc' :
                                  codeInfo.type === 'REV' ? '#facc15' :
                                  codeInfo.type === 'SUPPLY' ? '#fb923c' :
                                  codeInfo.type === 'RX' ? '#fb7185' :
                                  '#9ca3af'
                          }}
                        >
                          {codeInfo.type}
                        </span>
                      </TableCell>
                      {showNdcColumn && <TableCell className="font-mono text-xs text-muted-foreground">
                        {mapping.ndc_code || '—'}
                      </TableCell>}
                      <TableCell className="max-w-xs">
                        {(() => {
                          // Consumer-friendly name only applies to CPT/HCPCS (the
                          // procedures-table namespace). REV/DRG would collide with
                          // CPT numbers, so they fall through to the technical desc.
                          const procedureCode = mapping.cpt_code || mapping.hcpcs_code;
                          const procInfo = procedureCode ? consumerFriendlyNames[procedureCode] : null;
                          const consumerFriendlyName = procInfo?.name;
                          const technicalDescription = mapping.procedure_description || mapping.chargemaster_description;

                          if (consumerFriendlyName) {
                            return (
                              <div className="space-y-1">
                                <div className="font-semibold text-primary">"{consumerFriendlyName}"</div>
                                <div className="text-sm text-muted-foreground italic truncate">
                                  {technicalDescription}
                                </div>
                              </div>
                            );
                          }

                          return <div className="truncate">{technicalDescription}</div>;
                        })()}
                      </TableCell>
                      <TableCell className="text-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="px-3 py-1 rounded font-mono font-bold text-xs inline-block cursor-help"
                                style={{
                                  backgroundColor: badgeBg,
                                  borderColor: settingBadgeBorderColor,
                                  borderWidth: '1px',
                                  borderStyle: 'solid',
                                  color: settingBadgeTextColor
                                }}
                              >
                                {mapping.inpatient_outpatient === 'inpatient' && '🏥 INPATIENT'}
                                {mapping.inpatient_outpatient === 'outpatient' && '🚶 OUTPATIENT'}
                                {mapping.inpatient_outpatient === 'both' && '🏥🚶 BOTH'}
                                {(!mapping.inpatient_outpatient || mapping.inpatient_outpatient === '') && '❓ UNSPECIFIED'}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="max-w-xs">
                                {mapping.inpatient_outpatient === 'inpatient' && (
                                  <>
                                    <p className="font-semibold">🏥 {t('hospital:mappingsTable.settingTooltips.inpatientTitle')}</p>
                                    <p className="text-xs">{t('hospital:mappingsTable.settingTooltips.inpatientDescription')}</p>
                                  </>
                                )}
                                {mapping.inpatient_outpatient === 'outpatient' && (
                                  <>
                                    <p className="font-semibold">🚶 {t('hospital:mappingsTable.settingTooltips.outpatientTitle')}</p>
                                    <p className="text-xs">{t('hospital:mappingsTable.settingTooltips.outpatientDescription')}</p>
                                  </>
                                )}
                                {mapping.inpatient_outpatient === 'both' && (
                                  <>
                                    <p className="font-semibold">🏥🚶 {t('hospital:mappingsTable.settingTooltips.bothTitle')}</p>
                                    <p className="text-xs">{t('hospital:mappingsTable.settingTooltips.bothDescription')}</p>
                                  </>
                                )}
                                {(!mapping.inpatient_outpatient || mapping.inpatient_outpatient === '') && (
                                  <>
                                    <p className="font-semibold">❓ {t('hospital:mappingsTable.settingTooltips.unspecifiedTitle')}</p>
                                    <p className="text-xs">{t('hospital:mappingsTable.settingTooltips.unspecifiedDescription')}</p>
                                  </>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      {(() => {
                        // Use normalized price per unit if available, otherwise fall back to gross charge
                        const normalizedPrice = (mapping as any).normalized_price_per_unit;
                        const normalizedUnit = (mapping as any).normalized_unit;
                        const hospitalCharge = mapping.gross_charge || (mapping as any).averaged_price;

                        // Determine which price to display
                        const displayPrice = normalizedPrice || hospitalCharge;
                        const isNormalized = normalizedPrice && normalizedUnit && normalizedUnit !== 'other';

                        const isLowerThanNational = nationalAvg?.avg_gross_charge && displayPrice && displayPrice < nationalAvg.avg_gross_charge;

                        return (
                          <TableCell className={`text-right border-l-2 border-r-2 ${
                            isLowerThanNational
                              ? 'bg-green-500/10 border-l-green-500/30 border-r-green-500/30'
                              : 'bg-blood-red/10 border-l-blood-red/30 border-r-blood-red/30'
                          }`}>
                            <div className="flex items-center justify-end gap-2">
                              <div className={`font-mono font-bold text-base ${
                                isLowerThanNational ? 'text-green-500' : 'text-blood-red'
                              }`}>
                                {formatCurrency(displayPrice)}
                                {isNormalized && (
                                  <span className="text-xs text-muted-foreground ml-1">
                                    /{normalizedUnit}
                                  </span>
                                )}
                              </div>
                              {isNormalized && (
                                <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/20">
                                  {t('hospital:mappingsTable.priceSource.normalizedBadge', { unit: normalizedUnit })}
                                </Badge>
                              )}
                              {(mapping as any).price_source?.includes('estimated_') && !isNormalized && (
                                <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
                                  {t('hospital:mappingsTable.priceSource.averagedFromRange')}
                                </Badge>
                              )}
                            </div>
                            {isNormalized ? (
                              <div className="text-xs text-muted-foreground mt-1 text-right">
                                {t('hospital:mappingsTable.priceSource.normalizedPerUnit')}
                                {hospitalCharge && hospitalCharge !== displayPrice && (
                                  <div className="text-xs text-muted-foreground">
                                    ({t('hospital:mappingsTable.priceSource.total', { amount: formatCurrency(hospitalCharge) })})
                                  </div>
                                )}
                              </div>
                            ) : (mapping as any).price_source && (
                              <div className="text-xs text-muted-foreground mt-1 text-right">
                                {(mapping as any).price_source === 'exact_gross_charge' && t('hospital:mappingsTable.priceSource.hospitalProvided')}
                                {(mapping as any).price_source === 'estimated_from_min_max_average' && t('hospital:mappingsTable.priceSource.estimatedFromRange')}
                                {(mapping as any).price_source === 'estimated_from_min_rate_only' && t('hospital:mappingsTable.priceSource.estimatedFromMinRate')}
                                {(mapping as any).price_source === 'estimated_from_max_rate_only' && t('hospital:mappingsTable.priceSource.estimatedFromMaxRate')}
                                {(mapping as any).price_source === 'no_pricing_data_available' && t('hospital:mappingsTable.priceSource.noPricingData')}
                              </div>
                            )}
                          </TableCell>
                        );
                      })()}
                      <TableCell className="text-right font-mono">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex flex-col items-end gap-1 cursor-help">
                                <div className="flex items-center gap-1">
                                  {formatCurrency(nationalAvg?.avg_gross_charge)}
                                  {nationalAvg && <Info className="w-3 h-3 text-muted-foreground" />}
                                </div>
                                {nationalAvg && (
                                  <span
                                    className="px-2 py-0.5 rounded font-mono font-bold text-xs inline-block"
                                    style={{
                                      backgroundColor: badgeBg,
                                      borderColor: nationalAvg.matched_setting === 'both' ? '#a855f7' :
                                                  nationalAvg.matched_setting === 'all_settings' ? '#3b82f6' : '#10b981',
                                      borderWidth: '1px',
                                      borderStyle: 'solid',
                                      color: nationalAvg.matched_setting === 'both' ? '#a855f7' :
                                            nationalAvg.matched_setting === 'all_settings' ? '#3b82f6' : '#10b981'
                                    }}
                                  >
                                    {nationalAvg.matched_setting === 'unspecified'
                                      ? 'UNSPECIFIED'
                                      : nationalAvg.matched_setting === 'both'
                                      ? 'BOTH'
                                      : nationalAvg.matched_setting === 'all_settings'
                                      ? 'ALL SETTINGS'
                                      : nationalAvg.matched_setting.toUpperCase()
                                    }
                                    <br />
                                    [{nationalAvg.total_hospitals || '?'} hospitals]
                                  </span>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs max-w-xs">
                                {nationalAvg ? (
                                  <>
                                    <p className="font-semibold mb-1">{t('hospital:mappingsTable.nationalAvgTooltip.title')}</p>
                                    <p>{t('hospital:mappingsTable.nationalAvgTooltip.basedOn', { count: nationalAvg.total_hospitals })}</p>
                                    <p>{t('hospital:mappingsTable.nationalAvgTooltip.avgGross', { amount: formatCurrency(nationalAvg.avg_gross_charge) })}</p>
                                    <p>{t('hospital:mappingsTable.nationalAvgTooltip.avgCash', { amount: formatCurrency(nationalAvg.avg_discounted_cash) })}</p>
                                    {nationalAvg.matched_setting === 'both' ? (
                                      <p className="text-muted-foreground mt-1 text-xs border-t pt-1">
                                        {t('hospital:mappingsTable.nationalAvgTooltip.multipleSettings', { count: nationalAvg.settings_averaged })}
                                      </p>
                                    ) : nationalAvg.matched_setting === 'all_settings' ? (
                                      <p className="text-muted-foreground mt-1 text-xs border-t pt-1">
                                        {t('hospital:mappingsTable.nationalAvgTooltip.allSettings', { count: nationalAvg.settings_averaged })}
                                      </p>
                                    ) : (
                                      <p className="text-muted-foreground mt-1 text-xs border-t pt-1">
                                        {t('hospital:mappingsTable.nationalAvgTooltip.matchedTo', { setting: nationalAvg.matched_setting })}
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <p>{t('hospital:mappingsTable.nationalAvgTooltip.noData')}</p>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        <div className="flex flex-col items-end gap-1">
                          {formatPercentage(percentageDiff)}
                          {percentageDiff !== null && percentageDiff > 10000 && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-900/30 text-red-400 border border-red-500/30 cursor-help leading-tight inline-block">
                                    SUSPECT CODE
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="font-semibold text-red-400">Likely Incorrect Code Mapping</p>
                                  <p className="text-xs mt-1">This hospital's charge is {Math.round(percentageDiff / 100)}x the national average. This usually means the hospital assigned the wrong procedure code (e.g., an expensive drug mapped to a cheap generic's NDC code).</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {(() => {
                          const getCompareUrl = () => {
                            switch (codeInfo.type) {
                              case 'CPT':
                                return `/cpt/${codeInfo.code}`;
                              case 'DRG':
                                return `/drg/${codeInfo.code}`;
                              case 'REV':
                                return `/rev/${codeInfo.code}`;
                              case 'HCPCS':
                                return `/hcpcs/${codeInfo.code}`;
                              default:
                                return null;
                            }
                          };
                          
                          const compareUrl = getCompareUrl();
                          
                          return compareUrl ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300"
                              asChild
                            >
                              <a href={compareUrl} target="_blank" rel="noopener noreferrer" aria-label={`Compare national prices for ${codeInfo.type} ${codeInfo.code}`}>
                                <Eye className="h-4 w-4" />
                              </a>
                            </Button>
                          ) : (
                            <span className="text-muted-foreground text-xs">N/A</span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(mapping.discounted_cash_price)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(mapping.min_negotiated_rate)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(mapping.max_negotiated_rate)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          ); })()}
        </div>

        {/* Pagination Controls */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {t('hospital:mappingsTable.pagination.showing', { start: ((currentPage - 1) * pageSize) + 1, end: Math.min(currentPage * pageSize, totalCount), total: totalCount.toLocaleString() })}
              {selectedCodeType && ` ${t('hospital:mappingsTable.pagination.filteredBy', { type: selectedCodeType.toUpperCase() })}`}
              {searchTerm && ` ${t('hospital:mappingsTable.pagination.matching', { term: searchTerm })}`}
              {hospitalId === '4063e788-1130-4b32-98a0-8e09bbedd1b6' && (
                <div className="flex items-center gap-1 mt-1 text-xs text-blue-400">
                  <AlertTriangle className="w-3 h-3" />
                  <span>Q2055 records excluded due to unit measurement adjustments</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('common:pagination.previous')}
              </Button>

              <div className="flex items-center gap-1 text-sm">
                <span>{t('common:pagination.page')}</span>
                <span className="font-mono font-bold">{currentPage}</span>
                <span>{t('common:pagination.of')}</span>
                <span className="font-mono font-bold">{Math.ceil(totalCount / pageSize)}</span>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                className="flex items-center gap-1"
              >
                {t('common:pagination.next')}
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}